import { lightTheme } from "../styles/theme";

/**
 * Generate consistent avatar colors based on name using theme colors
 * @param name - The name to generate color for
 * @param theme - Optional theme object, defaults to lightTheme
 * @returns A color string from the theme's avatar colors
 */
export const getAvatarColor = (
  name: string,
  theme: any = lightTheme
): string => {
  const colors = theme?.colors?.avatarColors;

  if (!colors || colors.length === 0) {
    // Fallback to a default set of colors if theme colors are not available
    const defaultColors = [
      lightTheme.colors.avatarColors[0], // green
      lightTheme.colors.avatarColors[1], // purple
      lightTheme.colors.avatarColors[2], // yellow
      lightTheme.colors.navNoticeFeed, // pink
      lightTheme.colors.avatarColors[4], // emerald
      lightTheme.colors.avatarColors[5], // violet
      lightTheme.colors.avatarColors[6], // amber
      lightTheme.colors.avatarColors[7], // indigo
    ];
    const hash = name
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return defaultColors[hash % defaultColors.length];
  }

  const hash = name
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

/**
 * Get initials from a name (first letter of each word, max 2 characters)
 * @param name - The name to get initials from
 * @returns Uppercase initials string
 */
export const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Generate avatar color using theme palette colors
 * Uses neutral colors appropriate for avatars (avoids red/blue/warning which have specific UI meanings)
 * @param name - The name to generate color for
 * @param theme - Theme object with palette colors
 * @returns A color string from the theme's palette
 */
export const getThemeAvatarColor = (name: string, theme: any): string => {
  // Use theme's avatarColors array (already filtered to avoid red/blue)
  const avatarColors = theme?.colors?.avatarColors;

  if (!avatarColors || avatarColors.length === 0) {
    // Fallback to primary function if theme colors not available
    return getAvatarColor(name, theme);
  }

  const hash = name
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return avatarColors[hash % avatarColors.length];
};
